<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1100px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            color="primary" 
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon>mdi-gesture-tap-hold</v-icon>
          </v-btn>
        </template>
      
        <span>Update Approval</span>
      </v-tooltip>
    </template>
   
    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="hrissecondary"
      title="Claim Approval"
      text="Update Claim Approval Status"
      class="mb-0"
    >
      <template v-if="dialog && !loading">
        <v-container
          data-simplebar
          class="pt-4"
        >
          <v-row dense>
            <v-col cols="3">
              Requester:
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> {{ claimRequest.employee.person.name }} </span>
            </v-col>
            
            <v-col cols="3">
              Claim Cycle:
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> {{ claimRequest.claim_cycle.start_date + ' - ' + claimRequest.claim_cycle.end_date }} </span>
            </v-col>
            
            <v-col cols="3">
              Reference No.
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> {{ claimRequest.reference_number }} </span>
            </v-col>
            
            <v-col cols="3">
              Request Date
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> {{ claimRequest.request_date }} </span>
            </v-col>
            
            <v-col cols="3">
              Requested Amount
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> RM{{ thousandSeparator((claimRequest.total_request_amount / 100).toFixed(2)) }} </span>
            </v-col>
            
            <v-col cols="3">
              Remark
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="8">
              <span class="font-weight-bold"> {{ claimRequest.remark ? claimRequest.remark : '-' }} </span>
            </v-col>       
          </v-row>
          <v-data-table
            :headers="entryHeaders"
            :items="claimRequest.claim_request_entries"
            hide-default-footer
            disable-pagination
            class="mt-4"
          >
            <template v-slot:item.claim_type="{ item }">
              {{ item.claim_type.name }}
              <v-tooltip
                v-if="item.claim_request_entry_dates.length > 0 || item.entitlement_date_to_deduct || item.description || item.claim_request_custom_field_entries.length > 0"
                right
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    color="hrissecondary"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <div
                  v-for="item in item.claim_request_entry_dates"
                  :key="item.id"
                  class="font-weight-bold text-left"
                >
                  {{ $moment(item.date).format('YYYY-MM-DD') }} ({{ $moment(item.date).format('ddd') }})
                  {{ item.amount_to_deduct ? ': RM' + thousandSeparator((item.amount_to_deduct / 100).toFixed(2)) : '' }}
                </div> 
                <div 
                  v-if="item.claim_request_entry_employees.length > 0"
                  class="font-weight-bold text-left"
                >
                  With: 
                  <li
                    v-for="item in item.claim_request_entry_employees"
                    :key="item.id"
                    class="mx-5 text-left"
                  >
                    <ul class="font-weight-light text-left ml-n6 my-0 py-0">
                      {{ item.employee.person.name }}
                    </ul>
                  </li> 
                </div>
                <div
                  v-if="item.entitlement_date_to_deduct"
                  class="font-weight-bold text-left"
                >
                  Claim for {{ $moment(item.entitlement_date_to_deduct).format('MMM YYYY') }}
                </div> 
                <div
                  v-if="item.description"
                  class="text-left"
                >
                  <span class="font-weight-bold">Description: </span>
                  <span
                    v-html="item.description.replace(/\n/g, '</br>')"
                  />
                </div>
                <div
                  v-for="item in item.claim_request_custom_field_entries"
                  :key="item.id"
                  class="text-left"
                >
                  <span class="font-weight-bold">{{ item.claim_type_custom_field.name }}: </span>
                  <span>
                    {{ item.claim_type_custom_field.type === 'Selection' ? item.claim_type_custom_field_selection.name : item.value }}
                  </span>
                </div>
              </v-tooltip>
              <v-tooltip
                v-if="item.declaration_accepted && item.claim_type.declaration_text"
                right
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    color="green"
                    v-on="on"
                  >
                    mdi-checkbox-marked-circle-outline
                  </v-icon>
                </template>
                <span
                  class="text-left"
                  v-html="item.claim_type.declaration_text.replace(/\n/g, '</br>')"
                />
              </v-tooltip>
            </template>
            <template v-slot:item.receipt_date="{ item }">
              <span>
                {{ item.receipt_date ? $moment(item.receipt_date).format('DD MMM YYYY') : '-' }}
              </span>
            </template>
            <template v-slot:item.receipt_number="{ item }">
              <span>
                {{ item.receipt_number ? item.receipt_number : '-' }}
              </span>
            </template>
            <template v-slot:item.amount="{ item }">
              <span>
                {{ thousandSeparator((item.total_amount / 100).toFixed(2)) }}
              </span>
            </template>
            <template v-slot:item.view="{ item }">
              <v-row 
                v-for="document in item.documents"
                :key="document.id"
                class="my-1"
              >
                <v-chip
                  color="blue-grey lighten-3"
                  link
                  style="width: 100%;"
                  class="pl-3"
                  :href="document.file_url"
                  target="_blank"
                >
                  <v-icon
                    color="red"
                    left
                  >
                    mdi-file-image
                  </v-icon>
                  <span class="text-truncate font-10"> {{ document.file_name }} </span>
                </v-chip>
              </v-row>
            </template>
          </v-data-table>
        </v-container>
        <v-divider class="mx-0 mb-2" />
        <form>
          <v-row
            v-if="claimRequest.claim_request_escalation"
            no-gutters
            class="font-weight-bold"
          >
            <div>
              <v-fade-transition>
                <v-icon
                  :style="{
                    animationDuration: `0.6s`
                  }"
                  color="red"
                  class="mb-1 v-avatar--metronome white--text"
                  size="x-large"
                >
                  mdi-alert-circle
                </v-icon>
              </v-fade-transition>
              <span 
                class="ml-1 red--text"
              >
                Attention to superior: 
                Kindly note that {{ claimRequest.claim_request_escalation.created_by }} requested to escalate this claim request to {{ claimRequest.claim_request_escalation.employee.person.name }}
              </span>
            </div>
          </v-row>
          <v-container class="py-0">
            <v-row no-gutters>
              <v-col
                cols="12"
              >
                <v-radio-group
                  v-model="selectedStatus"
                  v-validate="'required'"
                  :error-messages="errors.collect('selectedStatus')"
                  data-vv-name="selectedStatus"        
                  data-vv-as="status"        
                  row
                >
                  <v-radio
                    v-for="item in filteredStatuses"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </v-radio-group>
              </v-col>
              <v-col
                v-if="selectedStatus === 270 && isApproveOnBehalf"
                cols="12"
              >
                <v-autocomplete
                  v-model="escalateTo"
                  v-validate="selectedStatus === 270? 'required' : ''"
                  :error-messages="errors.collect('escalateTo')"
                  :loading="approversLoading"
                  :items="approvers"
                  data-vv-name="escalateTo"        
                  data-vv-as="higher approver"        
                  label="Escalate To"
                  item-text="person.name"
                  item-value="employee_uid"
                />
              </v-col>
              <v-col
                cols="12"
              >
                <v-textarea
                  v-model="remark"
                  v-validate="(
                    selectedStatus === 300 || 
                    selectedStatus === 500 || 
                    selectedStatus === 502 || 
                    (selectedStatus === 270 && (!claimRequest.claim_request_escalation || isApproveOnBehalf))
                  )? 'required|max:2000' : ''"
                  :rows="2"
                  :error-messages="errors.collect('remark')"
                  data-vv-name="remark"        
                  label="Remark"
                />
              </v-col>
            </v-row>
          </v-container>
        </form>
      </template>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Update Status
        </v-btn>
      </v-card-actions>
    </material-card>

    <v-dialog
      v-model="emptyEntitlementDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="headline">
          No claim entitlement found
        </v-card-title>

        <v-card-text>
          Do you still want to proceed the approval?
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="progressing"
            text
            @click="emptyEntitlementDialog = false"
          >
            No
          </v-btn>
          <v-btn
            :loading="progressing"
            color="hrissecondary"
            class="white--text"
            @click="callAprroveApi"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
  import { mapMutations } from 'vuex'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      claimRequest: {
        type: Object,
        required: true,
      },
      isApproveOnBehalf: {
        type: Boolean,
        required: true,
      },
      isAssignedToMe: {
        type: Boolean,
        required: true,
      },
    },  
    data () {
      return {
        dialog: null,
        loading: false,
        progressing: false,
        statuses: [
          {
            id: 200,
            name: 'Approve',
          },
          {
            id: 300,
            name: 'Request More',
          },
          {
            id: 500,
            name: 'Reject',
          },
        ],
        entryHeaders: [
          {
            text: 'Claim Type',
            align: 'left',
            value: 'claim_type',
            sortable: false,
          },
          {
            text: 'Receipt Date',
            align: 'center',
            value: 'receipt_date',
            sortable: false,
          },
          {
            text: 'Receipt No.',
            align: 'center',
            value: 'receipt_number',
            sortable: false,
          },
          {
            text: 'Amount (RM)',
            align: 'right',
            value: 'amount',
            sortable: false,
          },
          {
            text: 'Attachment',
            align: 'center',
            value: 'view',
            width: '280px',
            sortable: false,
          },
        ],
        selectedStatus: null,
        remark: null,
        emptyEntitlementDialog: false,
        ignoreEmptyEntitlement: false,
        hasClaimThatRequireHigherLevelApproval: false,
        approvals: [],
        approvers: [],
        approversLoading: false, 
        escalateTo: null,
      }
    },
    computed: {
      filteredStatuses () {
        if (
          this.hasClaimThatRequireHigherLevelApproval &&
          (
            this.claimRequest.approval_request.latest_approval.approval_status_type_id === 201 ||
            this.claimRequest.approval_request.latest_approval.approval_status_type_id === 270 ||
            this.claimRequest.approval_request.latest_approval.approval_status_type_id === 502 ||
            (
              this.claimRequest.claim_approval_workflow_batch &&
              ((this.claimRequest.claim_approval_workflow_batch.superior_approval_level === 1 && this.claimRequest.approval_request.latest_approval.approval_status_type_id === 100) ||
                (this.claimRequest.claim_approval_workflow_batch.superior_approval_level !== 1 && this.claimRequest.approval_request.latest_approval.approval_status_type_id === 260))
            )
          )
        ) {
          return [
            ...this.statuses, 
            {
              id: 270,
              name: 'Escalate',
            },
          ] 
        }
        
        if (
          this.hasClaimThatRequireHigherLevelApproval &&
          this.claimRequest.approval_request.latest_approval.approval_status_type_id === 250 && 
          this.claimRequest.approval_request.latest_approval.workflowable &&
          !this.claimRequest.approval_request.latest_approval.workflowable.approvable_id
        ) {
          return [
            ...this.statuses, 
            {
              id: 502,
              name: 'Revert',
            },
          ] 
        }

        return this.statuses
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return 

        this.requireHigherLevelApproval()
      },
      selectedStatus (value) {
        if (this.isApproveOnBehalf && value === 270 && this.approvers.length <= 0) {
          this.approversLoading = true
        
          this.$api.hrisEmployees().list('?employee_uid=' + this.claimRequest.employee_uid)
            .then((res) => {
              this.approvers = _.orderBy(res.data, ['person.name'], ['asc'])
              this.approversLoading = false
            })
        } else {
          this.approvers = []
        }
      },
    },
    methods: {
      ...mapMutations('app', ['setClaimPendingCount']),
      requireHigherLevelApproval () {
        this.loading = true

        this.hasClaimThatRequireHigherLevelApproval = this.claimRequest.claim_request_entries.some(
          e => e.claim_type && e.claim_type.allow_higher_level_approval === 1
        )

        this.loading = false
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      callAprroveApi () {
        this.ignoreEmptyEntitlement = true
        this.progressing = true

        this.approve()
          .then(response => {
            this.$emit('updated')
            this.$snack({ message: response.data.status })

            this.$api.claimApprovals().pendingCount()
              .then(response => {
                this.setClaimPendingCount(response.data)
              })
              .catch(() => {})

            this.ignoreEmptyEntitlement = false
            this.emptyEntitlementDialog = false
            this.selectedStatus = null
            this.remark = null
            this.dialog = false
          })
          .catch((e) => {
            this.$snack({ 
              color: 'error',
              message: e.response.data.message,
            })
          })
          .then(() => {
            this.progressing = false
          })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.update()
              .then(response => {
                this.$emit('updated')
                this.$snack({ message: response.data.status })

                this.$api.claimApprovals().pendingCount()
                  .then(response => {
                    this.setClaimPendingCount(response.data)
                  })
                  .catch(() => {})

                this.selectedStatus = null
                this.remark = null
                this.dialog = false
              })
              .catch((e) => {
                if (e.response.status === 400) {
                  this.emptyEntitlementDialog = true
                } else {
                  this.$snack({ 
                    color: 'error',
                    message: e.response.data.message,
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      update () {
        if (this.selectedStatus === 200) {
          return this.approve()
        } else if (this.selectedStatus === 300) {
          return this.requestMore()
        } else if (this.selectedStatus === 270) {
          return this.escalate()
        } else if (this.selectedStatus === 502) {
          return this.revert()
        } else {
          return this.reject()
        }
      },
      approve () {
        return this.$api.claimApprovals().approve(this.claimRequest.approval_request.latest_approval.id, { isApproveOnBehalf: this.isApproveOnBehalf, remark: this.remark, ignoreEmptyEntitlement: this.ignoreEmptyEntitlement, isAssignedToMe: this.isAssignedToMe })
      },
      reject () {
        return this.$api.claimApprovals().reject(this.claimRequest.approval_request.latest_approval.id, { isApproveOnBehalf: this.isApproveOnBehalf, remark: this.remark, isAssignedToMe: this.isAssignedToMe })
      },
      requestMore () {
        return this.$api.claimApprovals().requestMore(this.claimRequest.approval_request.latest_approval.id, { isApproveOnBehalf: this.isApproveOnBehalf, remark: this.remark, isAssignedToMe: this.isAssignedToMe })
      },
      escalate () {
        return this.$api.claimApprovals().escalate(this.claimRequest.approval_request.latest_approval.id, { isApproveOnBehalf: this.isApproveOnBehalf, remark: this.remark, escalateTo: this.escalateTo, isAssignedToMe: this.isAssignedToMe })
      },
      revert () {
        return this.$api.claimApprovals().revert(this.claimRequest.approval_request.latest_approval.id, { isApproveOnBehalf: this.isApproveOnBehalf, remark: this.remark, isAssignedToMe: this.isAssignedToMe })
      },
    },
  }
</script>

<style scoped>
.font-10{
  font-size:10px !important;
}
.v-data-table ::v-deep td{
  font-size: 13.5px !important;
}

@keyframes metronome-example {
  from {
    transform: scale(.5);
  }

  to {
    transform: scale(1);
  }
}

.v-avatar--metronome {
  animation-name: metronome-example;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}
</style>
